/**
 * Training Generation Hooks
 *
 * TanStack Query hooks for AI training video generation.
 * Used for viewing generation jobs, triggering manual generation,
 * and tracking progress.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

// ============================================
// TYPES
// ============================================

export interface TrainingGenerationJob {
  id: string;
  status: 'queued' | 'generating' | 'uploading' | 'completed' | 'failed';
  operator: {
    id: string;
    name: string;
    employeeId: string;
    email?: string;
  } | null;
  assessment?: {
    id: string;
    overallScore: number;
    assessedAt: string;
  } | null;
  sop: {
    id: string;
    title: string;
    version?: string;
  } | null;
  task: {
    id: string;
    title: string;
    description?: string;
    steps?: Array<{
      id: string;
      stepNumber: number;
      description: string;
    }>;
  } | null;
  skillGapDescription: string;
  currentScore: number;
  targetScore: number;
  prompt?: string;
  soraJobId?: string;  // legacy DB column name (maps to Veo job ID)
  soraModel?: string;  // legacy DB column name (maps to Veo model used)
  generatedVideoS3Key?: string;
  videoDurationSeconds?: number;
  generatedMaterial: {
    id: string;
    title: string;
    description?: string;
    duration?: number;
  } | null;
  generatedAssignment: {
    id: string;
    status: string;
    progress?: number;
    dueDate: string | null;
    createdAt?: string;
  } | null;
  errorMessage?: string;
  retryCount: number;
  createdAt: string;
  completedAt?: string;
}

export interface TrainingGenerationListResponse {
  data: TrainingGenerationJob[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface TrainingGenerationConfig {
  autoGenerateThreshold: number;
  minAssessmentsBeforeGeneration: number;
  defaultDurationSeconds: number;
  maxDurationSeconds: number;
  provider: string;
}

export interface TrainingGenerationStats {
  total: number;
  byStatus: {
    queued: number;
    generating: number;
    uploading: number;
    completed: number;
    failed: number;
  };
  recentCompletions: number;
}

export interface TriggerGenerationParams {
  assessmentId: string;
  taskId?: string;
}

// ============================================
// QUERY KEYS
// ============================================

export const trainingGenerationKeys = {
  all: ['training-generation'] as const,
  lists: () => [...trainingGenerationKeys.all, 'list'] as const,
  list: (params: { page?: number; status?: string; operatorId?: string }) =>
    [...trainingGenerationKeys.lists(), params] as const,
  details: () => [...trainingGenerationKeys.all, 'detail'] as const,
  detail: (id: string) => [...trainingGenerationKeys.details(), id] as const,
  config: () => [...trainingGenerationKeys.all, 'config'] as const,
  stats: () => [...trainingGenerationKeys.all, 'stats'] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Fetch training generation jobs list
 */
export function useTrainingGenerationJobs(params: {
  page?: number;
  limit?: number;
  status?: string;
  operatorId?: string;
} = {}) {
  const api = useApi();
  const { page = 1, limit = 20, status, operatorId } = params;

  return useQuery({
    queryKey: trainingGenerationKeys.list({ page, status, operatorId }),
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', page.toString());
      searchParams.set('limit', limit.toString());
      if (status) searchParams.set('status', status);
      if (operatorId) searchParams.set('operatorId', operatorId);

      return api.get<TrainingGenerationListResponse>(
        `/api/training-generation?${searchParams.toString()}`
      );
    },
  });
}

/**
 * Fetch single training generation job details
 */
export function useTrainingGenerationJob(jobId: string) {
  const api = useApi();

  return useQuery({
    queryKey: trainingGenerationKeys.detail(jobId),
    queryFn: () => api.get<TrainingGenerationJob>(`/api/training-generation/${jobId}`),
    enabled: !!jobId,
  });
}

/**
 * Fetch training generation configuration
 */
export function useTrainingGenerationConfig() {
  const api = useApi();

  return useQuery({
    queryKey: trainingGenerationKeys.config(),
    queryFn: () => api.get<TrainingGenerationConfig>('/api/training-generation/config'),
    staleTime: 1000 * 60 * 60, // 1 hour - config rarely changes
  });
}

/**
 * Fetch training generation stats
 */
export function useTrainingGenerationStats() {
  const api = useApi();

  return useQuery({
    queryKey: trainingGenerationKeys.stats(),
    queryFn: () => api.get<TrainingGenerationStats>('/api/training-generation/stats'),
  });
}

/**
 * Trigger training generation mutation
 */
export function useTriggerTrainingGeneration() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: TriggerGenerationParams) =>
      api.post<{ message: string; queueJobId: string; assessmentId: string }>(
        '/api/training-generation',
        params
      ),
    onSuccess: () => {
      // Invalidate jobs list and stats
      queryClient.invalidateQueries({ queryKey: trainingGenerationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: trainingGenerationKeys.stats() });
    },
  });
}

/**
 * Cancel training generation job mutation
 */
export function useCancelTrainingGeneration() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) =>
      api.delete<{ message: string }>(`/api/training-generation/${jobId}`),
    onSuccess: () => {
      // Invalidate jobs list and stats
      queryClient.invalidateQueries({ queryKey: trainingGenerationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: trainingGenerationKeys.stats() });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get status badge color
 */
export function getStatusColor(status: TrainingGenerationJob['status']): string {
  switch (status) {
    case 'queued':
      return 'bg-gray-100 text-gray-700';
    case 'generating':
      return 'bg-blue-100 text-blue-700';
    case 'uploading':
      return 'bg-purple-100 text-purple-700';
    case 'completed':
      return 'bg-emerald-100 text-emerald-700';
    case 'failed':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

/**
 * Get status display text
 */
export function getStatusText(status: TrainingGenerationJob['status']): string {
  switch (status) {
    case 'queued':
      return 'Queued';
    case 'generating':
      return 'Generating Video';
    case 'uploading':
      return 'Uploading';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

/**
 * Format duration in seconds to human readable
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (remainingSeconds === 0) {
    return `${minutes}m`;
  }
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}
